import React from 'react';
import { PencilIcon } from '@heroicons/react/solid';

/**
 * getOrdinalSuffix
 * ----------------
 * Returns the ordinal suffix for a number (st, nd, rd, th).
 * Examples: 1 → "st", 2 → "nd", 3 → "rd", 4 → "th", 11 → "th", 21 → "st"
 */
const getOrdinalSuffix = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Month names for date formatting
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

/**
 * formatDate
 * ----------
 * Formats an ISO date string (YYYY-MM-DD) into a human-friendly format.
 * 
 * Example: "2025-07-18" → "18th July 2025"
 * 
 * Used for general date fields (not date of birth).
 * 
 * @param {string} isoDate - ISO format date string (YYYY-MM-DD)
 * @returns {string} Formatted date string, or original value if not a valid date
 */
const formatDate = (isoDate) => {
  if (typeof isoDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return isoDate;
  }

  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return isoDate;
  }

  const day = date.getDate();
  const dayWithOrdinal = `${day}${getOrdinalSuffix(day)}`;
  const monthName = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();

  return `${dayWithOrdinal} ${monthName} ${year}`;
};

/**
 * formatDateWithAge
 * -----------------
 * Formats an ISO date string (YYYY-MM-DD) into a human-friendly format with the
 * person's age calculated from today's date.
 * 
 * Example: "2018-07-05" → "5th July 2018 (6 years old)"
 * 
 * This is specifically for Date of Birth fields where knowing the age at a glance
 * is helpful for referrers reviewing their submission.
 * 
 * @param {string} isoDate - ISO format date string (YYYY-MM-DD)
 * @returns {string} Formatted date string with age, or original value if not a valid date
 */
const formatDateWithAge = (isoDate) => {
  if (typeof isoDate !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
    return isoDate;
  }

  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return isoDate;
  }

  // Calculate age in years from today
  const today = new Date();
  let ageYears = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  // Adjust age if birthday hasn't occurred yet this year
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate())) {
    ageYears--;
  }

  const day = date.getDate();
  const dayWithOrdinal = `${day}${getOrdinalSuffix(day)}`;
  const monthName = MONTH_NAMES[date.getMonth()];
  const year = date.getFullYear();
  const ageText = ageYears === 1 ? '1 year old' : `${ageYears} years old`;

  return `${dayWithOrdinal} ${monthName} ${year} (${ageText})`;
};

/**
 * isDobField
 * ----------
 * Determines if a question is a Date of Birth field based on its questionId or label.
 * DOB fields get special formatting that includes the calculated age.
 * 
 * @param {object} question - The question object with questionId and/or label
 * @returns {boolean} True if this is a DOB field
 */
const isDobField = (question) => {
  const id = (question?.questionId || '').toLowerCase();
  const label = (question?.label || '').toLowerCase();
  return id.includes('dob') || id.includes('date_of_birth') || label.includes('date of birth');
};

/**
 * formatDateValue
 * ---------------
 * Formats a date value appropriately based on whether it's a DOB field or not.
 * - DOB fields: "5th July 2018 (6 years old)"
 * - Other dates: "18th July 2025"
 * 
 * @param {string} value - The ISO date string
 * @param {object} question - The question object to determine if it's a DOB
 * @returns {string} Formatted date string
 */
const formatDateValue = (value, question) => {
  return isDobField(question) ? formatDateWithAge(value) : formatDate(value);
};

/**
 * ReviewAnswer
 * The formatted answer for one review step: dates with ages, chosen option
 * labels, nominations, measurements, and "No answer given" for empty optional
 * questions. Shared by the review lists so every review reads answers the
 * same way.
 */
export function ReviewAnswer({ step: s, answers = {} }) {
  if (!s) return null;
  if (s.type === 'group') {
    return (
      <div className="space-y-1">
        {(s.children || []).map((child) => {
          const v = answers[child.questionId];
          const isEmpty =
            v == null ||
            (Array.isArray(v) ? v.length === 0 : String(v).trim() === '');
          const noAnswer = !child.required && isEmpty;
          // Format date fields - DOB gets age, other dates just formatted nicely
          const displayValue = child.type === 'date' ? formatDateValue(v, child) : String(v || '');
          return (
            <div key={child.questionId} className="text-sm text-gray-700">
              <span className="font-medium mr-2">{child.label}:</span>
              {noAnswer ? (
                <span className="italic text-gray-500">No answer given</span>
              ) : (
                <>{displayValue}</>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  const v = answers[s.questionId];
  if (s.type === 'repeatable_group') {
    const items = Array.isArray(v) ? v : [];
    if (!items.length) return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
    return (
      <div className="space-y-2">
        {items.map((row, idx) => (
          <div key={row.__key || idx} className="p-2 border border-gray-100 rounded">
            {(s.children || []).map((ch) => (
              <div key={ch.questionId} className="text-sm text-gray-700">
                <span className="font-medium mr-2">{ch.label}:</span>
                {(() => {
                  const rv = row[ch.questionId];
                  if (ch.type === 'radio') {
                    const opt = (ch.options || []).find(o => (rv?.optionId ?? rv) === o.optionId);
                    if (!opt) return '';
                    if (opt.allowsFreeText && rv?.otherText) return `${opt.label}: ${rv.otherText}`;
                    return opt.label;
                  }
                  // Format date fields - DOB gets age, other dates just formatted nicely
                  if (ch.type === 'date') {
                    return formatDateValue(rv, ch);
                  }
                  return String(rv || '');
                })()}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  if (s.type === 'checkboxes') {
    const arr = Array.isArray(v) ? v : [];
    if (!arr.length) return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
    const labels = arr.map((item) => {
      const id = item?.optionId ?? item;
      const opt = (s.options || []).find(o => o.optionId === id);
      if (!opt) return '';
      if (opt.allowsFreeText && item?.otherText) return `${opt.label}: ${item.otherText}`;
      return opt.label;
    }).filter(Boolean);
    if (labels.length <= 1) return labels[0] || '';
    return (
      <ul className="list-disc pl-5 space-y-1">
        {labels.map((lab, idx) => (
          <li key={idx}>{lab}</li>
        ))}
      </ul>
    );
  }
  if (s.type === 'radio') {
    const opt = (s.options || []).find(o => (v?.optionId ?? v) === o.optionId);
    if (!opt) return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
    if (opt.allowsFreeText && v?.otherText) return `${opt.label}: ${v.otherText}`;
    return opt.label;
  }
  if (s.type === 'address') {
    const hasAny = !!(v?.addr_street || v?.addr_city || v?.addr_postcode);
    if (!hasAny && !s.required) return <span className="italic text-gray-500">No answer given</span>;
    return `${v?.addr_street || ''}, ${v?.addr_city || ''}, ${v?.addr_postcode || ''}`;
  }
  if (s.type === 'schoolSelect') {
    if (!v?.schoolId) return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
    const addr = v.schoolAddress || {};
    const addrLine = [addr.line1, addr.city, addr.postcode].filter(Boolean).join(', ');
    return addrLine ? `${v.schoolName} (${addrLine})` : v.schoolName;
  }
  // Format date fields - DOB gets age, other dates just formatted nicely
  if (s.type === 'date') {
    const isEmpty = v == null || String(v).trim() === '';
    if (isEmpty && !s.required) return <span className="italic text-gray-500">No answer given</span>;
    return formatDateValue(v, s);
  }
  // Handle milestone age group fields (developmental milestones)
  // Data shape: { milestoneId: { age: number|null, unit: 'months'|'years', notSure: boolean, notYetDoing: boolean } }
  if (s.type === 'milestoneAgeGroup') {
    const milestones = s.milestones || [];
    if (!v || typeof v !== 'object') {
      return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
    }
    // Check if at least one milestone has been answered (age, notSure, or notYetDoing)
    const hasAnyAnswer = milestones.some((m) => {
      const entry = v[m.milestoneId];
      return entry && (entry.age != null || entry.notSure === true || entry.notYetDoing === true);
    });
    if (!hasAnyAnswer) {
      return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
    }
    return (
      <ul className="list-disc pl-5 space-y-1">
        {milestones.map((m) => {
          const entry = v[m.milestoneId];
          if (!entry) return <li key={m.milestoneId}>{m.label}: Not specified</li>;
          if (entry.notYetDoing) return <li key={m.milestoneId}>{m.label}: Not yet doing</li>;
          if (entry.notSure) return <li key={m.milestoneId}>{m.label}: Not sure</li>;
          if (entry.age == null) return <li key={m.milestoneId}>{m.label}: Not specified</li>;
          return <li key={m.milestoneId}>{m.label}: {entry.age} {entry.unit || 'months'}</li>;
        })}
      </ul>
    );
  }
  // Handle physical health measurement fields (height/weight, head circ, pulse, BP)
  // Data shape varies by measurementType — see PhysicalHealthInfoGroupField.js for details
  if (s.type === 'physicalHealthInfoGroup') {
    if (!v || typeof v !== 'object') {
      return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
    }
    const mt = s.measurementType;
    // Helper: format month/year date or "Not sure"
    const fmtDate = (obj) => {
      if (!obj) return '';
      if (obj.dateNotSure) return ' (date: not sure)';
      if (obj.date?.month && obj.date?.year) {
        const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        return ` (${months[obj.date.month - 1]} ${obj.date.year})`;
      }
      return '';
    };
    const lines = [];
    if (mt === 'height_weight') {
      const w = v.weight || {};
      const h = v.height || {};
      if (w.value != null) lines.push(`Weight: ${w.value} ${w.unit || 'kg'}${fmtDate(w)}`);
      if (h.unit === 'ft_in' && (h.ft != null || h.in != null)) {
        lines.push(`Height: ${h.ft || 0}ft ${h.in || 0}in${fmtDate(h)}`);
      } else if (h.value != null) {
        lines.push(`Height: ${h.value} ${h.unit || 'cm'}${fmtDate(h)}`);
      }
    } else if (mt === 'head_circumference') {
      // "I don't know" stores { notSure: true } with no value — it is an
      // answer, and the review must say so in the respondent's own words.
      if (v.notSure) lines.push("Head circumference: I don't know");
      else if (v.value != null) lines.push(`Head circumference: ${v.value} ${v.unit || 'cm'}${fmtDate(v)}`);
    } else if (mt === 'pulse_rate') {
      if (v.unable) lines.push('Unable to take measurement');
      else if (v.value != null) lines.push(`Pulse: ${v.value} bpm${fmtDate(v)}`);
    } else if (mt === 'blood_pressure') {
      if (v.unable) lines.push('Unable to take measurement');
      else if (v.systolic != null || v.diastolic != null) {
        lines.push(`BP: ${v.systolic ?? '-'}/${v.diastolic ?? '-'} mmHg${fmtDate(v)}`);
      }
    }
    if (lines.length === 0) {
      return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
    }
    return (
      <ul className="list-disc pl-5 space-y-1">
        {lines.map((line, i) => <li key={i}>{line}</li>)}
      </ul>
    );
  }
  // Handle nomination fields (e.g., legal guardian nomination, additional responsible persons)
  // These store an object or array of objects with nominee details that should be displayed as formatted fields
  // Field names are dynamically detected to support different naming conventions (guardian_*, lg_lr_*, etc.)
  if (s.type === 'nomination') {
    // Helper to extract display label from a value that may be:
    // - A string directly
    // - An object with { optionId, label } (from select fields)
    // - An object with just { optionId } (from radio fields - no label stored)
    // When only optionId is available, convert to readable format
    const getDisplayLabel = (val) => {
      if (!val) return null;
      if (typeof val === 'string') return val;
      if (typeof val === 'object') {
        // Prefer label if available
        if (val.label) return val.label;
        // Otherwise, convert optionId to readable format
        // e.g., "dont_know" → "Don't know", "yes" → "Yes"
        if (val.optionId) {
          const id = val.optionId;
          // Handle common known values for better display
          if (id === 'yes') return 'Yes';
          if (id === 'no') return 'No';
          if (id === 'dont_know' || id === 'not_sure') return "Don't know / Not sure";
          if (id === 'sometimes') return 'Sometimes';
          if (id === 'part_time') return 'Part Time';
          // Generic fallback: replace underscores, capitalize first letter
          return id.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
        }
      }
      return null;
    };
    
    // Dynamically find field names from children schema for flexible field name support
    const children = s.children || [];
    const findFieldId = (patterns, type = null) => {
      const field = children.find(c => {
        if (type && c.type !== type) return false;
        return patterns.some(p => c.questionId.includes(p) || c.label?.toLowerCase().includes(p));
      });
      return field?.questionId;
    };
    
    const firstNameFieldId = findFieldId(['first_name', 'first']);
    const lastNameFieldId = findFieldId(['last_name', 'last']);
    const emailFieldId = children.find(c => c.type === 'email' && !c.questionId.includes('confirm'))?.questionId;
    const phoneFieldId = children.find(c => c.type === 'phone' && !c.questionId.includes('confirm'))?.questionId;
    const relationshipFieldId = findFieldId(['relationship']);
    const livesWithFieldId = findFieldId(['lives_with']);
    const militaryFieldId = findFieldId(['military']);
    
    // Helper to render a single nomination object
    const renderNomination = (nomination, index = null) => {
      const firstName = firstNameFieldId ? (nomination[firstNameFieldId] || '') : '';
      const lastName = lastNameFieldId ? (nomination[lastNameFieldId] || '') : '';
      const email = emailFieldId ? nomination[emailFieldId] : null;
      const phone = phoneFieldId ? (nomination[`${phoneFieldId}_normalized`] || nomination[phoneFieldId]) : null;
      const relationshipLabel = relationshipFieldId ? getDisplayLabel(nomination[relationshipFieldId]) : null;
      const livesWithLabel = livesWithFieldId ? getDisplayLabel(nomination[livesWithFieldId]) : null;
      const militaryLabel = militaryFieldId ? getDisplayLabel(nomination[militaryFieldId]) : null;
      const fullName = `${firstName} ${lastName}`.trim();
      
      return (
        <div key={index} className={`space-y-1 ${index !== null && index > 0 ? 'mt-3 pt-3 border-t border-gray-200' : ''}`}>
          {fullName && <div><span className="font-medium">Name:</span> {fullName}</div>}
          {relationshipLabel && <div><span className="font-medium">Relationship:</span> {relationshipLabel}</div>}
          {email && <div><span className="font-medium">Email address:</span> {email}</div>}
          {phone && <div><span className="font-medium">Phone number:</span> {phone}</div>}
          {livesWithLabel && <div><span className="font-medium">Lives with CYP?:</span> {livesWithLabel}</div>}
          {militaryLabel && <div><span className="font-medium">In armed forces?:</span> {militaryLabel}</div>}
        </div>
      );
    };
    
    // Handle both single (object) and multiple (array) nomination modes
    const allowMultiple = s.meta?.allowMultiple === true;
    
    if (allowMultiple) {
      // Multiple mode: v should be an array
      const nominations = Array.isArray(v) ? v : (v && typeof v === 'object' ? [v] : []);
      if (nominations.length === 0) {
        return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
      }
      return (
        <div>
          {nominations.map((nomination, idx) => renderNomination(nomination, idx))}
        </div>
      );
    } else {
      // Single mode: v should be an object
      const hasValue = v && typeof v === 'object' && (
        (firstNameFieldId && v[firstNameFieldId]) || 
        (emailFieldId && v[emailFieldId])
      );
      if (!hasValue) return s.required ? '' : <span className="italic text-gray-500">No answer given</span>;
      return renderNomination(v);
    }
  }
  {
    const isEmpty = v == null || String(v).trim() === '';
    if (isEmpty && !s.required) return <span className="italic text-gray-500">No answer given</span>;
  }
  return String(v || '');

}

/**
 * ReviewList
 * Displays a list of review cards for each visible step with a snapshot of the current answer.
 * Clicking a card (or pressing Enter/Space when focused) invokes onEdit(index).
 *
 * readOnly renders the same cards with no edit affordance at all — no button
 * role, no pencil, no hover — for answers locked by the respondent's
 * confirm-and-submit step (TDI-415), where offering an edit the server will
 * refuse would be dishonest.
 */
export default function ReviewList({ steps = [], answers = {}, onEdit, readOnly = false }) {
  const handleKey = (e, i) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (typeof onEdit === 'function') onEdit(i);
    }
  };

  const cardClassName = readOnly
    ? 'p-3 rounded-lg border border-gray-200 flex items-start gap-3'
    : 'transition-all duration-300 ease-out p-3 rounded-lg border border-gray-200 hover:bg-secondary/10 hover:shadow hover:border-secondary cursor-pointer flex items-start gap-3';
  const cardInteractiveProps = (i) => (readOnly ? {} : {
    role: 'button',
    tabIndex: 0,
    title: 'Edit answer',
    onClick: () => onEdit?.(i),
    onKeyDown: (e) => handleKey(e, i),
  });

  // Group steps by sectionTitle to add subsection headers
  const groups = [];
  steps.forEach((s, i) => {
    const title = s.sectionTitle || '';
    const last = groups[groups.length - 1];
    const item = { step: s, index: i };
    if (!last || last.title !== title) {
      groups.push({ title, items: [item] });
    } else {
      last.items.push(item);
    }
  });

  return (
    <div className="w-full md:w-1/2 mx-auto space-y-3">
      {groups.map((g, gi) => (
        <div key={`${g.title || 'untitled'}_${gi}`} className="space-y-3">
          {g.title ? (
            <div className="font-semibold mt-2 mb-1">{g.title}</div>
          ) : null}
          {g.items.map(({ step: s, index: i }) => {
        if (s.type === 'group') {
          return (
            <div
              key={s.questionId}
                  className={cardClassName}
              {...cardInteractiveProps(i)}
            >
                  {!readOnly && <PencilIcon className="mt-1 w-4 h-4 text-gray-500" />}
                  <div className="flex-1">
              <div className="text-sm font-semibold text-gray-800 mb-2">{s.label}</div>
              <ReviewAnswer step={s} answers={answers} />
              </div>
            </div>
          );
        }
        return (
          <div
            key={s.questionId}
                className={cardClassName}
            {...cardInteractiveProps(i)}
          >
                {!readOnly && <PencilIcon className="mt-1 w-4 h-4 text-gray-500" />}
                <div className="flex-1">
            <div className="text-sm font-semibold text-gray-800 mb-1">{s.label}</div>
            <div className="text-sm text-gray-700">
              <ReviewAnswer step={s} answers={answers} />
                  </div>
            </div>
          </div>
        );
      })}
        </div>
      ))}
    </div>
  );
}


